import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getTwitchLive, LiveChannel } from "../../services/TwitchLiveService";
import styles from "./LiveEventStream.module.css";

// "72 Games på 72 timer" (2–4 Nov 2026). Renders only while a channel is live, never from
// Thursday 5 Nov 00:00 Danish time. The API applies the same cutoff and fails closed.
const EVENT_END = Date.parse("2026-11-05T00:00:00+01:00");
const PROGRAM_URL = "https://72timer.powerstats.dk";
const POLL_MS = 60_000;

export default function LiveEventStream() {
    const { t } = useTranslation();
    const [channels, setChannels] = useState<LiveChannel[]>([]);
    const [selected, setSelected] = useState<string | null>(null);

    useEffect(() => {
        if (Date.now() >= EVENT_END) return;
        let cancelled = false;

        const load = () => {
            if (document.visibilityState !== "visible") return;
            if (Date.now() >= EVENT_END) {
                setChannels([]);
                return;
            }
            getTwitchLive()
                .then((all) => {
                    if (!cancelled) setChannels(all.filter((c) => c.live));
                })
                .catch(() => {
                    // Keep the last known state; a single failed poll shouldn't make the player flicker.
                });
        };

        load();
        const interval = setInterval(load, POLL_MS);
        document.addEventListener("visibilitychange", load);
        return () => {
            cancelled = true;
            clearInterval(interval);
            document.removeEventListener("visibilitychange", load);
        };
    }, []);

    if (channels.length === 0) return null;

    // Mesterlund first (API order); falls back if the picked channel goes offline.
    const active = channels.find((c) => c.login === selected) ?? channels[0];
    const playerSrc =
        `https://player.twitch.tv/?channel=${encodeURIComponent(active.login)}` +
        `&parent=${encodeURIComponent(window.location.hostname)}&muted=true&autoplay=true`;

    return (
        <section className={styles.live} aria-label={t("pages:liveEvent.title")}>
            <div className={styles.player}>
                <iframe
                    src={playerSrc}
                    title={`${active.name} – Twitch`}
                    allow="autoplay; fullscreen"
                    allowFullScreen
                />
            </div>

            <div className={styles.side}>
                <span className={styles.badge}>
                    <span className={styles.dot} aria-hidden="true" />
                    {t("pages:liveEvent.badge")}
                </span>
                <h2 className={styles.title}>{t("pages:liveEvent.title")}</h2>
                <p className={styles.subtitle}>{t("pages:liveEvent.subtitle")}</p>

                {channels.length > 1 && (
                    <div className={styles.tabs} role="group" aria-label={t("pages:liveEvent.chooseStream")}>
                        {channels.map((c) => (
                            <button
                                key={c.login}
                                type="button"
                                className={`${styles.tab} ${c.login === active.login ? styles.tabActive : ""}`}
                                aria-pressed={c.login === active.login}
                                onClick={() => setSelected(c.login)}
                            >
                                {c.name}
                            </button>
                        ))}
                    </div>
                )}

                <div className={styles.actions}>
                    <a
                        href={`https://www.twitch.tv/${active.login}`}
                        target="_blank"
                        rel="noreferrer"
                        className={`${styles.btn} ${styles.btnTwitch}`}
                    >
                        {t("pages:liveEvent.watchOnTwitch")}
                    </a>
                    <a href={PROGRAM_URL} target="_blank" rel="noreferrer" className={`${styles.btn} ${styles.btnGhost}`}>
                        {t("pages:liveEvent.program")} →
                    </a>
                </div>
            </div>
        </section>
    );
}
